import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ShieldCheck, AlertCircle, AlertTriangle, Info, Wand2, RefreshCw, X } from "lucide-react";
import { cn } from "@/lib/utils";

const SEV = {
  error: { icon: AlertCircle, cls: "text-[color:var(--danger)]", label: "Errors" },
  warning: { icon: AlertTriangle, cls: "text-[#B45309]", label: "Warnings" },
  info: { icon: Info, cls: "text-[color:var(--brand-ocean)]", label: "Info" },
};

// Validate Model: side sheet with issues grouped by severity + one-click fixes.
export function ModelValidationSheet({ open, onOpenChange, contracts, onEditContract, onChanged }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get("/join-contracts/validate"); setRes(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Validation failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open) run(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const byId = Object.fromEntries((contracts || []).map((c) => [c.id, c]));

  const deactivateOthers = async (ids) => {
    const [keep, ...rest] = ids;
    try {
      await Promise.all(rest.map((id) => api.patch(`/join-contracts/${id}`, { active: false })));
      toast.success(`Kept "${byId[keep]?.name}", set ${rest.length} inactive`);
      onChanged && onChanged(); run();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not update"); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[420px] sm:max-w-[420px] flex flex-col" data-testid="model-validate-sheet">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[color:var(--brand-ocean)]" />Validate model</SheetTitle>
          <SheetDescription>Schema drift, key type mismatches, ambiguous paths, duplicates, loops and unrelated datasets.</SheetDescription>
        </SheetHeader>
        <div className="flex items-center gap-2 py-2">
          {res && (
            <div data-testid="model-validate-summary" className="flex items-center gap-1.5 text-[11px]">
              <Badge variant="outline" className={cn("text-[10px]", res.summary.errors ? "border-[color:var(--danger)] text-[color:var(--danger)]" : "")}>{res.summary.errors} errors</Badge>
              <Badge variant="outline" className={cn("text-[10px]", res.summary.warnings ? "border-[#F59E0B] text-[#B45309]" : "")}>{res.summary.warnings} warnings</Badge>
              <Badge variant="outline" className="text-[10px]">{res.summary.relationships} relationships · {res.summary.datasets} datasets</Badge>
            </div>
          )}
          <Button data-testid="model-validate-rerun" size="sm" variant="ghost" className="h-7 ml-auto" onClick={run} disabled={busy}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}</Button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto space-y-3 pr-1">
          {!res ? <Loader2 className="w-4 h-4 animate-spin" /> : res.issues.length === 0 ? (
            <div data-testid="model-validate-ok" className="rounded-xl border border-[color:var(--glass-border)] p-4 text-center text-sm text-[color:var(--ink-2)]">
              <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-[color:var(--success)]" />Model is healthy — every relationship resolves and no paths are ambiguous.
            </div>
          ) : ["error", "warning", "info"].map((sev) => {
            const items = res.issues.filter((i) => i.severity === sev);
            if (!items.length) return null;
            const S = SEV[sev];
            return (
              <div key={sev}>
                <p className={cn("text-[10px] uppercase tracking-wide mb-1 flex items-center gap-1", S.cls)}><S.icon className="w-3 h-3" />{S.label} · {items.length}</p>
                <div className="space-y-1.5">
                  {items.map((it, i) => (
                    <div key={i} data-testid="model-validate-issue" data-code={it.code} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 text-[11px] text-[color:var(--ink-2)]">
                      <p>{it.message}</p>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {it.contract_id && byId[it.contract_id] && <Button data-testid="model-validate-open-rel" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={() => onEditContract(byId[it.contract_id])}>Open relationship</Button>}
                        {it.code === "ambiguous" && it.contract_ids?.length > 1 && <Button data-testid="model-validate-fix-ambiguous" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={() => deactivateOthers(it.contract_ids)}>Keep first, deactivate others</Button>}
                        {it.code === "duplicate" && it.contract_ids?.length > 1 && <Button size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={() => onEditContract(byId[it.contract_ids[1]])}>Review duplicate</Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Model-wide auto-detect: propose relationships for every unrelated pair; accept/reject each.
export function AutodetectAllDialog({ open, onOpenChange, datasets, onCreated }) {
  const [sugg, setSugg] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [dismissedCount, setDismissedCount] = useState(0);
  const load = (includeDismissed) => {
    setSugg(null); setPicked(new Set());
    api.post("/join-contracts/autodetect-all", { dataset_ids: datasets.map((d) => d.id), include_dismissed: includeDismissed })
      .then(({ data }) => {
        setSugg(data.suggestions); setDismissedCount(data.dismissed_count || 0);
        setPicked(new Set(data.suggestions.map((s, i) => (!s.dismissed && s.confidence >= 0.85 ? i : -1)).filter((i) => i >= 0)));
      })
      .catch((e) => { toast.error(e?.response?.data?.detail || "Auto-detect failed"); setSugg([]); });
  };
  useEffect(() => { if (open) { setShowDismissed(false); load(false); } }, [open, datasets]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (i) => setPicked((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const dismiss = async (s, restore = false) => {
    try {
      await api.post("/join-contracts/autodetect-all/dismiss", { left_dataset_id: s.left_dataset_id, right_dataset_id: s.right_dataset_id, restore });
      toast.success(restore ? "Suggestion restored" : "Suggestion dismissed — it won't be proposed again");
      load(showDismissed);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  const accept = async () => {
    const items = (sugg || []).filter((_, i) => picked.has(i)).map((s) => ({
      name: s.name, left_dataset_id: s.left_dataset_id, right_dataset_id: s.right_dataset_id, keys: s.keys, cardinality: s.cardinality || "many_to_one", cross_filter_direction: "single", active: true }));
    if (!items.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/join-contracts/bulk", { items });
      toast.success(`${data.created.length} relationship(s) created${data.failed.length ? `, ${data.failed.length} skipped` : ""}`);
      data.failed.forEach((f) => toast.warning(`${f.name}: ${typeof f.detail === "string" ? f.detail : f.detail?.message}`));
      onCreated(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create relationships"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="autodetect-all-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Wand2 className="w-5 h-5 text-[color:var(--brand-mint)]" />Auto-detect relationships</DialogTitle>
          <DialogDescription>Suggested joins between datasets that aren't related yet, ranked by confidence. Accept the ones that make sense.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-auto space-y-1.5">
          {!sugg ? <Loader2 className="w-4 h-4 animate-spin" /> : !sugg.length ? (
            <p data-testid="autodetect-all-empty" className="text-sm text-[color:var(--ink-3)] p-3 text-center">No new relationships detected — every likely pair is already related or shares no key-like columns.</p>
          ) : sugg.map((s, i) => (
            <label key={i} data-testid="autodetect-suggestion" data-dismissed={s.dismissed ? "true" : undefined} className={cn("flex items-center gap-3 rounded-lg border px-3 py-2 text-xs cursor-pointer", picked.has(i) ? "border-[color:var(--brand-ocean)] bg-[color:rgba(43,78,255,0.05)]" : "border-[color:var(--glass-border)]", s.dismissed && "opacity-60")}>
              <Checkbox data-testid="autodetect-pick" checked={picked.has(i)} onCheckedChange={() => toggle(i)} disabled={s.dismissed} />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[color:var(--ink)] truncate">{s.left_name} <span className="text-[color:var(--ink-3)]">→</span> {s.right_name}{s.dismissed && <span className="ml-1 text-[10px] text-[color:var(--ink-3)]">· dismissed</span>}</div>
                <div className="font-mono text-[10px] text-[color:var(--ink-3)]">{s.keys.map((k) => `${k.left_field} = ${k.right_field}`).join(", ")} · {s.reason}</div>
              </div>
              <Badge variant="outline" className={cn("text-[10px]", s.confidence >= 0.85 ? "border-[color:var(--success)] text-[color:var(--success)]" : "")}>{Math.round(s.confidence * 100)}%</Badge>
              {s.dismissed
                ? <Button data-testid="autodetect-restore" size="sm" variant="ghost" className="h-6 text-[10px] text-[color:var(--brand-ocean)]" onClick={(e) => { e.preventDefault(); dismiss(s, true); }}>Restore</Button>
                : <Button data-testid="autodetect-dismiss" size="icon" variant="ghost" title="Reject — don't suggest this pair again" className="h-6 w-6 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]" onClick={(e) => { e.preventDefault(); dismiss(s); }}><X className="w-3.5 h-3.5" /></Button>}
            </label>
          ))}
        </div>
        <DialogFooter className="items-center">
          {dismissedCount > 0 && (
            <button data-testid="autodetect-toggle-dismissed" className="text-[11px] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] mr-auto"
              onClick={() => { const v = !showDismissed; setShowDismissed(v); load(v); }}>{showDismissed ? "Hide dismissed" : `Show ${dismissedCount} dismissed`}</button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="autodetect-accept" disabled={busy || !picked.size} onClick={accept} className="bg-[color:var(--brand-ocean)] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Create {picked.size} relationship{picked.size === 1 ? "" : "s"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
